import { Download, ListChecks, Pencil, Printer, Undo2 } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';

import { usePermission } from '@/hooks/usePermission';
import { useBackState } from '@/lib/back-nav';
import { capabilityNote, type CapabilityState } from '@/lib/document-capabilities';

import { PrintCopyModal, type CopyMode } from './PrintCopyModal';

// Rail de acciones del expediente. "Editar en línea" y "Devolver" llevan a la pantalla
// Editor y flujo (so=290). La vista con marca de agua y la descarga generan una copia
// controlada con folio (so=400), solo si el documento lo permite a su rol (permiso global
// o regla del tipo, spec 2026-10-14 §8 D6; si una regla lo quita, se dice por qué); el registro de copias
// del documento se abre ya filtrado, solo con permiso de bitácora. Cada acción aparece
// solo con su permiso; sin ninguna (proveedor con acceso temporal) no hay tarjeta.

export function ActionRail({
  entryId,
  code,
  print,
  canReturn,
}: {
  entryId: string;
  code: string;
  /** Imprimir y descargar sobre ESTE documento, calculado por el servidor. */
  print: CapabilityState;
  /** El servidor ofrece una devolución habilitada para ESTE documento (no basta el rol: el
   *  botón llevaría a un editor sin la acción). */
  canReturn: boolean;
}): ReactElement | null {
  const canOpenEditor = usePermission('quality:master-list:read');
  const canPrint = print.allowed;
  // Se explica lo que el usuario no puede deducir (regla del tipo, estado del documento);
  // sin permiso del rol, el botón simplemente no aparece.
  const printNote =
    !print.allowed && print.reason !== 'missing_permission'
      ? capabilityNote(print, 'imprimir')
      : null;
  const canLog = usePermission('quality:print-log:read');
  const [mode, setMode] = useState<CopyMode | null>(null);
  const editor = `/documentos/${entryId}/editor`;
  // El editor vuelve a esta ficha (y la ficha, a su propio origen).
  const fromHere = useBackState(`Ficha ${code}`);
  if (!canOpenEditor && !canPrint && !canLog && printNote === null) {
    return null;
  }
  return (
    <div className="card rise d1">
      <div className="ch">
        <h3>Acciones</h3>
      </div>
      <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {canOpenEditor && (
          <Link to={editor} state={fromHere} className="btn">
            <Pencil size={16} aria-hidden="true" />
            Editar en línea y flujo
          </Link>
        )}
        {canOpenEditor && canReturn && (
          <Link to={editor} state={fromHere} className="btn danger">
            <Undo2 size={16} aria-hidden="true" />
            Devolver a etapa previa
          </Link>
        )}
        {canPrint && (
          <>
            <button type="button" className="btn mut" onClick={() => setMode('print_view')}>
              <Printer size={16} aria-hidden="true" />
              Vista con marca de agua
            </button>
            <button type="button" className="btn mut" onClick={() => setMode('download')}>
              <Download size={16} aria-hidden="true" />
              Descargar PDF
            </button>
          </>
        )}
        {printNote !== null && (
          <p className="soft" style={{ fontSize: 12, margin: 0 }}>
            {printNote}
          </p>
        )}
        {canLog && (
          <Link to={`/marca-de-agua?doc=${encodeURIComponent(entryId)}`} className="btn mut">
            <ListChecks size={16} aria-hidden="true" />
            Ver registro de copias
          </Link>
        )}
      </div>
      <PrintCopyModal mode={mode} entryId={entryId} code={code} onClose={() => setMode(null)} />
    </div>
  );
}
